import React, { useState, useRef } from 'react';
import { Box, Button, Typography, IconButton, FormControlLabel, RadioGroup, Radio, FormControl, FormLabel, Slider } from '@mui/material';
import { CloudUpload, Close, Transform, FitScreen, CenterFocusStrong, Fullscreen, RestartAlt } from '@mui/icons-material';
import ImagePreviewWithGrid from './ImagePreviewWithGrid';
import { convertImageToChart, loadImage } from './imageConversionUtils';

export default function ImageUploader({ 
  onConvert, 
  onClose, 
  rows, 
  cols,
  cellSize = 16,
  colors
}) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [conversionMode, setConversionMode] = useState('existing'); // 'existing' or 'new'
  const [imageData, setImageData] = useState(null);
  const [imageTransform, setImageTransform] = useState({ x: 0, y: 0, scale: 1 });
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setImageUrl(url);
      setSelectedImage(file);
      
      try {
        const imageElement = await loadImage(url);
        
        setImageData({
          file,
          url,
          width: imageElement.width,
          height: imageElement.height,
          naturalWidth: imageElement.naturalWidth,
          naturalHeight: imageElement.naturalHeight,
          element: imageElement
        });
        
        // Set initial transform to top-left (0,0) with no scaling
        setImageTransform({ x: 0, y: 0, scale: 1 });
      } catch (error) {
        console.error('Error loading image:', error);
      }
    }
  };

  const handleConvert = async () => {
    if (!selectedImage || !imageUrl || !imageData || !imageData.element) return;
    
    try {
      const result = convertImageToChart(
        imageData.element,
        imageTransform,
        rows,
        cols,
        cellSize,
        colors,
        conversionMode
      );
      
      // Call parent's convert handler with the result
      onConvert(result, conversionMode);
      
    } catch (error) {
      console.error('Error converting image:', error);
    }
  };

  const handleFitImage = () => {
    if (!imageData || !imageData.element) return;
    
    const chartWidth = cols * cellSize;
    const chartHeight = rows * cellSize;
    
    // Fit: Scale to fit entirely within chart (maintains aspect ratio, may leave empty space)
    const scaleX = chartWidth / imageData.element.width;
    const scaleY = chartHeight / imageData.element.height;
    const scale = Math.min(scaleX, scaleY); // Use minimum to ensure it fits entirely
    
    // Center the fitted image
    const scaledWidth = imageData.element.width * scale;
    const scaledHeight = imageData.element.height * scale;
    const x = (chartWidth - scaledWidth) / 2;
    const y = (chartHeight - scaledHeight) / 2;
    
    setImageTransform({ x, y, scale });
  };

  const handleFillChart = () => {
    if (!imageData || !imageData.element) return;
    
    const chartWidth = cols * cellSize;
    const chartHeight = rows * cellSize;
    
    // Fill: Scale to fill entire chart (maintains aspect ratio, may crop image)
    const scaleX = chartWidth / imageData.element.width;
    const scaleY = chartHeight / imageData.element.height;
    const scale = Math.max(scaleX, scaleY); // Use maximum to ensure it fills entirely
    
    // Center the filled image
    const scaledWidth = imageData.element.width * scale;
    const scaledHeight = imageData.element.height * scale;
    const x = (chartWidth - scaledWidth) / 2;
    const y = (chartHeight - scaledHeight) / 2;
    
    setImageTransform({ x, y, scale });
  };

  const handleCenterImage = () => {
    if (!imageData || !imageData.element) return;
    
    const chartWidth = cols * cellSize;
    const chartHeight = rows * cellSize;
    
    const scaledWidth = imageData.element.width * imageTransform.scale;
    const scaledHeight = imageData.element.height * imageTransform.scale;
    const x = (chartWidth - scaledWidth) / 2;
    const y = (chartHeight - scaledHeight) / 2;
    
    setImageTransform(prev => ({ ...prev, x, y }));
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setImageUrl(url);
      setSelectedImage(file);
      
      try {
        const imageElement = await loadImage(url);
        
        setImageData({
          file,
          url,
          width: imageElement.width,
          height: imageElement.height,
          naturalWidth: imageElement.naturalWidth,
          naturalHeight: imageElement.naturalHeight,
          element: imageElement
        });
        
        // Set initial transform to top-left (0,0) with no scaling
        setImageTransform({ x: 0, y: 0, scale: 1 });
      } catch (error) {
        console.error('Error loading image:', error);
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  return (
    <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1, bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Image to Chart Converter</Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Box>

      {!selectedImage ? (
        <Box
          sx={{
            border: '2px dashed #ccc',
            borderRadius: 1,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover'
            }
          }}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body1" gutterBottom>
            Click to upload or drag and drop an image
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Supported formats: JPG, PNG, GIF
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </Box>
      ) : (
        <Box>
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <img
              src={imageUrl}
              alt="Selected"
              style={{
                maxWidth: '100%',
                maxHeight: '200px',
                objectFit: 'contain',
                border: '1px solid #ddd',
                borderRadius: 4
              }}
            />
          </Box>

          {/* Preview with Grid */}
          {imageData && imageTransform && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Preview with Grid Overlay
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                This preview updates in real-time as you adjust the image in the chart editor.
              </Typography>
              <ImagePreviewWithGrid
                imageData={imageData}
                imageTransform={imageTransform}
                rows={rows}
                cols={cols}
                cellSize={cellSize}
              />
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Image will be converted to a {rows} × {cols} grid. Use the controls below, or close this dialog and use Shift+drag/scroll in the main chart editor for fine adjustments.
            {imageData && imageData.element && (
              <Box component="span" sx={{ display: 'block', mt: 1, fontWeight: 'bold' }}>
                Image: {imageData.element.width} × {imageData.element.height}px
                {imageTransform && (
                  <span> | Position: ({Math.round(imageTransform.x)}, {Math.round(imageTransform.y)}) | Scale: {(imageTransform.scale * 100).toFixed(0)}%</span>
                )}
              </Box>
            )}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FitScreen />}
              onClick={handleFitImage}
              disabled={!imageData}
              title="Scale image to fit entirely within chart (maintains aspect ratio)"
            >
              Fit to Chart
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Fullscreen />}
              onClick={handleFillChart}
              disabled={!imageData}
              title="Scale image to fill entire chart (maintains aspect ratio, may crop)"
            >
              Fill Chart
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CenterFocusStrong />}
              onClick={handleCenterImage}
              disabled={!imageData}
              title="Center image at current scale"
            >
              Center Image
            </Button>
          </Box>

          {/* Fine Control Sliders */}
          {imageData && imageTransform && (
            <Box sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Fine Controls
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAlt />}
                  onClick={() => setImageTransform({ x: 0, y: 0, scale: 1 })}
                  title="Reset to original position and scale"
                >
                  Reset
                </Button>
              </Box>
              
              {/* Scale Slider */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Scale: {(imageTransform.scale * 100).toFixed(0)}%
                </Typography>
                <Slider
                  value={imageTransform.scale}
                  onChange={(event, newValue) => {
                    setImageTransform({
                      ...imageTransform,
                      scale: newValue
                    });
                  }}
                  min={0.1}
                  max={3}
                  step={0.05}
                  size="small"
                  marks={[
                    { value: 0.5, label: '50%' },
                    { value: 1, label: '100%' },
                    { value: 2, label: '200%' }
                  ]}
                />
              </Box>

              {/* X Position Slider */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  X Position: {Math.round(imageTransform.x)}px
                </Typography>
                <Slider
                  value={imageTransform.x}
                  onChange={(event, newValue) => {
                    setImageTransform({
                      ...imageTransform,
                      x: newValue
                    });
                  }}
                  min={-cols * cellSize}
                  max={cols * cellSize}
                  step={1}
                  size="small"
                  marks={[
                    { value: 0, label: '0' },
                    { value: cols * cellSize / 2, label: 'Center' }
                  ]}
                />
              </Box>

              {/* Y Position Slider */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" gutterBottom>
                  Y Position: {Math.round(imageTransform.y)}px
                </Typography>
                <Slider
                  value={imageTransform.y}
                  onChange={(event, newValue) => {
                    setImageTransform({
                      ...imageTransform,
                      y: newValue
                    });
                  }}
                  min={-rows * cellSize}
                  max={rows * cellSize}
                  step={1}
                  size="small"
                  marks={[
                    { value: 0, label: '0' },
                    { value: rows * cellSize / 2, label: 'Center' }
                  ]}
                />
              </Box>
            </Box>
          )}

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend">Conversion Mode</FormLabel>
            <RadioGroup
              value={conversionMode}
              onChange={(e) => setConversionMode(e.target.value)}
              row
            >
              <FormControlLabel
                value="existing"
                control={<Radio />}
                label="Use existing palette"
              />
              <FormControlLabel
                value="new"
                control={<Radio />}
                label="Add new colors to palette"
              />
            </RadioGroup>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<Transform />}
              onClick={handleConvert}
              fullWidth
            >
              Convert to Chart
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setSelectedImage(null);
                setImageUrl(null);
                if (imageUrl) {
                  URL.revokeObjectURL(imageUrl);
                }
              }}
            >
              Remove
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
